import {
  Action,
  Card,
  CardSkeleton,
  Dropdown,
  ICON_ALERT_CIRCLE_OUTLINE,
  ICON_LAND_PLOTS,
  Message,
  Stack,
  Text,
  SimpleTable,
  SimpleTableSkeleton,
  useNotifications,
} from '@doveit/bricks';
import objectHash from 'object-hash';
import React from 'react';
import { isEqual } from 'lodash';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { Property, PropertyUnit } from '../../../providers/api/dtos';
import useProperty from '../../hooks/use-property/useProperty';
import ViewPropertyUnitCard from '../view-property-unit-card/ViewPropertyUnitCard';
import ViewPropertyUnitTableRow from '../view-property-unit-table-row/ViewPropertyUnitTableRow';
import UpsertPropertyUnitAction from '../upsert-property-unit-action/UpsertPropertyUnitAction';
import { updateProperty } from '../../../providers/api/property/propertyProvider';

export const LOAD_PROPERTY_ERROR_MESSAGE = 'Non è stato possibile recuperare i dati dell\'immobile.';
export const EMPTY_PROPERTY_CADASTRAL_UNITS_INFO_MESSAGE = 'Non sono presenti unità immobiliari.';

export interface PropertyCadastralRegistryWidgetProps {
  propertyId: NonNullable<Property['id']>,
  canCreatePropertyUnit?: boolean,
  canUpdatePropertyUnit?: boolean,
  canDeletePropertyUnit?: boolean,
}

const PropertyCadastralRegistryWidget: React.FC<PropertyCadastralRegistryWidgetProps> = ({
  propertyId,
  canCreatePropertyUnit = false,
  canUpdatePropertyUnit = false,
  canDeletePropertyUnit = false,
}) => {
  const isMobile = useIsDevice('mobile');
  const [isSaving, setIsSaving] = React.useState(false);
  const { addError, addSuccess } = useNotifications();

  const {
    data: property,
    isLoading: isPropertyLoading,
    error: propertyError,
    mutate: mutateProperty,
  } = useProperty(propertyId);

  const propertyUnits = React.useMemo(() => property?.cadastralRegistry?.units || [], [property?.cadastralRegistry?.units]);

  const handlePropertyUnitCreate = React.useCallback(async (propertyUnit: PropertyUnit) => {
    if (!property) return;

    setIsSaving(true);

    try {
      const propertyToUpdate: Property = {
        ...property,
        cadastralRegistry: {
          ...property?.cadastralRegistry,
          units: [...propertyUnits, propertyUnit],
        },
      };

      await updateProperty(propertyId, propertyToUpdate);

      mutateProperty();

      addSuccess("L'unità immobiliare è stata aggiunta con successo");
    } catch (err) {
      addError("Non è stato possibile aggiungere l'unità immobiliare");
    }

    setIsSaving(false);
  }, [addError, addSuccess, propertyUnits, mutateProperty, property, propertyId]);

  const handlePropertyUnitUpdate = React.useCallback(async (updatedPropertyUnit: PropertyUnit, oldPropertyUnit: PropertyUnit) => {
    if (!property) return;

    setIsSaving(true);

    try {
      const propertyToUpdate: Property = {
        ...property,
        cadastralRegistry: {
          ...property?.cadastralRegistry,
          units: propertyUnits.map((unit) => (isEqual(unit, oldPropertyUnit) ? updatedPropertyUnit : unit)),
        },
      };

      await updateProperty(propertyId, propertyToUpdate);

      mutateProperty();

      addSuccess("L'unità immobiliare è stata modificata con successo");
    } catch (err) {
      addError("Non è stato possibile modificare l'unità immobiliare");
    }

    setIsSaving(false);
  }, [addError, addSuccess, propertyUnits, mutateProperty, property, propertyId]);

  const handlePropertyUnitDelete = React.useCallback(async (deletedPropertyUnit: PropertyUnit) => {
    if (!property) return;

    setIsSaving(true);

    try {
      const propertyToUpdate: Property = {
        ...property,
        cadastralRegistry: {
          ...property?.cadastralRegistry,
          units: propertyUnits.filter((unit) => !isEqual(unit, deletedPropertyUnit)),
        },
      };

      await updateProperty(propertyId, propertyToUpdate);

      mutateProperty();

      addSuccess("L'unità immobiliare è stata eliminata con successo");
    } catch (err) {
      addError("Non è stato possibile eliminare l'unità immobiliare");
    }

    setIsSaving(false);
  }, [addError, addSuccess, propertyUnits, mutateProperty, property, propertyId]);

  if (isPropertyLoading) {
    return (
      <CardSkeleton aria-label="Dati catastali in caricamento">
        {!isMobile && <SimpleTableSkeleton />}
      </CardSkeleton>
    );
  }

  if (propertyError) {
    return (
      <Card>
        <Card.Header
          icon={{ path: ICON_ALERT_CIRCLE_OUTLINE }}
          title="Dati catastali"
          color="critical"
        />
        <Card.Content>
          <Message message={LOAD_PROPERTY_ERROR_MESSAGE} />
        </Card.Content>
      </Card>
    );
  }

  return property! && (
    <Card aria-label="Sezione dati catastali">
      <Card.Header
        title="Dati catastali"
        icon={{ path: ICON_LAND_PLOTS }}
        primaryActions={[
          (!isMobile && canCreatePropertyUnit && (
            <UpsertPropertyUnitAction
              onSuccess={handlePropertyUnitCreate}
            >
              {({ upsert }) => (
                <Action
                  label="Aggiungi"
                  aria-label="Pulsante per aggiungere una nuova unità immobiliare"
                  size="S"
                  onClick={upsert}
                  loading={isSaving}
                />
              )}
            </UpsertPropertyUnitAction>
          )),
        ]}
        secondaryActions={[
          (isMobile && canCreatePropertyUnit && (
            <UpsertPropertyUnitAction
              onSuccess={handlePropertyUnitCreate}
            >
              {({ upsert }) => (
                <Dropdown.Option
                  label="Aggiungi"
                  aria-label="Pulsante per aggiungere una nuova unità immobiliare"
                  onClick={upsert}
                  loading={isSaving}
                />
              )}
            </UpsertPropertyUnitAction>
          )),
        ]}
      />
      <Card.Content>
        <Stack gap={150} aria-label="Lista unità">
          {isMobile && propertyUnits.length > 0 && (
            propertyUnits.map((unit) => (
              <ViewPropertyUnitCard
                key={objectHash(unit)}
                propertyUnit={unit}
                canUpdate={canUpdatePropertyUnit}
                canDelete={canDeletePropertyUnit}
                onUpdate={handlePropertyUnitUpdate}
                onDelete={handlePropertyUnitDelete}
              />
            ))
          )}
          {!isMobile && propertyUnits.length > 0 && (
            <SimpleTable aria-label="Tabella dati catastali">
              <SimpleTable.Header>
                <SimpleTable.HeaderCell>
                  <Text.BodySmall>Sezione</Text.BodySmall>
                </SimpleTable.HeaderCell>
                <SimpleTable.HeaderCell>
                  <Text.BodySmall>Foglio</Text.BodySmall>
                </SimpleTable.HeaderCell>
                <SimpleTable.HeaderCell>
                  <Text.BodySmall>Part.</Text.BodySmall>
                </SimpleTable.HeaderCell>
                <SimpleTable.HeaderCell>
                  <Text.BodySmall>Sub.</Text.BodySmall>
                </SimpleTable.HeaderCell>
                <SimpleTable.HeaderCell>
                  <Text.BodySmall>Cat.</Text.BodySmall>
                </SimpleTable.HeaderCell>
                <SimpleTable.HeaderCell>
                  <Text.BodySmall>Dest. d&apos;uso</Text.BodySmall>
                </SimpleTable.HeaderCell>
                <SimpleTable.HeaderCell>
                  <Text.BodySmall>Rendita</Text.BodySmall>
                </SimpleTable.HeaderCell>
                <SimpleTable.HeaderCell>
                  <Text.BodySmall>Classe Energ.</Text.BodySmall>
                </SimpleTable.HeaderCell>
                <SimpleTable.HeaderCell />
              </SimpleTable.Header>
              <SimpleTable.Body>
                {propertyUnits.map((unit) => (
                  <SimpleTable.Row key={objectHash(unit)} asChild>
                    <ViewPropertyUnitTableRow
                      propertyUnit={unit}
                      canUpdate={canUpdatePropertyUnit}
                      canDelete={canDeletePropertyUnit}
                      onDelete={handlePropertyUnitDelete}
                      onUpdate={handlePropertyUnitUpdate}
                      padded
                    />
                  </SimpleTable.Row>
                ))}
              </SimpleTable.Body>
            </SimpleTable>
          )}
          {propertyUnits.length === 0 && <Message message={EMPTY_PROPERTY_CADASTRAL_UNITS_INFO_MESSAGE} />}
        </Stack>
      </Card.Content>
    </Card>
  );
};

export default PropertyCadastralRegistryWidget;
